// RigSentinel — Owner Dashboard V1 (read-only).
//
// Server-authoritative: every fetch below sends the session cookie only;
// the backend independently verifies role === "owner" per request (see
// owner_dashboard.py). This page renders NOTHING itself as "owner" — it
// simply reflects whatever the API returns or denies.
//
// ABSOLUTE PRIVACY BOUNDARY: this page and its API never touch wells,
// rigs, BHA, kill sheets, or any engineering content — only account,
// entitlement, Paddle, and non-sensitive product-usage telemetry.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { fetchOverview } from "@/lib/ownerDashboardApi";
import { ChartCard, TrendLineChart, MixBarChart, MixPieChart, FunnelBars } from "@/components/owner-dashboard/OwnerCharts";
import OwnerUsersTable from "@/components/owner-dashboard/OwnerUsersTable";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"]];

function StatCard({ label, value, testId, accent }) {
  return (
    <div className="card-rs p-3" data-testid={testId}>
      <div className="text-[10px] label-up text-[var(--rs-text-3)]">{label}</div>
      <div className="text-lg font-bold mt-1" style={accent ? { color: accent } : undefined}>{value ?? "—"}</div>
    </div>
  );
}

function StatGrid({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map((it) => <StatCard key={it.label} {...it} />)}
    </div>
  );
}

function pct(num, den) {
  if (!den) return "Insufficient data";
  return `${Math.round((num / den) * 1000) / 10}%`;
}

// The API contract is server-owned and sections can legitimately be absent
// (feature not yet tracked, Paddle unreachable, telemetry window empty).
// Every nested read below goes through these so a missing section renders
// an empty/"—" state instead of throwing and blanking the whole page.
const _obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const _arr = (v) => (Array.isArray(v) ? v : []);
const _money = (v) => (v == null ? "—" : `$${v}`);
const _dateOr = (iso, fallback) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleString();
};

function HealthPanel({ title, testId, rows }) {
  return (
    <section className="card-rs p-4" data-testid={testId}>
      <h3 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">{title}</h3>
      <div className="space-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between text-[12px]">
            <span className="text-[var(--rs-text-3)]">{label}</span>
            <span className="font-mono-rs text-[var(--rs-text)]">{value ?? "—"}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// Per-account MRR contributions — makes the headline run-rate auditable
// instead of a single opaque number. Only accounts with a live Paddle
// subscription contribute, so an empty table with a non-zero MRR would
// itself be a signal worth investigating.
function MrrBreakdown({ rows, total }) {
  return (
    <section className="card-rs p-4 mt-3" data-testid="owner-mrr-breakdown">
      <h3 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">MRR Breakdown</h3>
      {rows.length === 0 ? (
        <p className="text-[12px] text-[var(--rs-text-3)]" data-testid="owner-mrr-breakdown-empty">
          No accounts are currently contributing to MRR.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px] border-collapse">
            <thead>
              <tr className="text-[var(--rs-text-3)] text-left">
                <th className="py-1 pr-3 font-normal">Account</th>
                <th className="py-1 pr-3 font-normal">Plan</th>
                <th className="py-1 pr-3 font-normal">Billing</th>
                <th className="py-1 pr-3 font-normal text-right">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((_r, i) => {
                const r = _obj(_r);
                return (
                  <tr key={r.subscriptionId || i}
                      data-testid={`owner-mrr-row-${i}`}
                      className="border-t border-[var(--rs-border)]">
                    <td className="py-1.5 pr-3 break-all">{r.email || "—"}</td>
                    <td className="py-1.5 pr-3">
                      {r.tier || "—"}{r.founding500 ? " · Founding" : ""}
                    </td>
                    <td className="py-1.5 pr-3">
                      {r.cadence || "—"}{r.status && r.status !== "active" ? ` · ${r.status}` : ""}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono-rs">{_money(r.monthlyContribution)}</td>
                  </tr>
                );
              })}
              <tr className="border-t border-[var(--rs-border)]">
                <td className="py-1.5 pr-3 font-semibold" colSpan={3}>Total</td>
                <td className="py-1.5 pr-3 text-right font-mono-rs font-semibold"
                    style={{ color: "var(--rs-amber)" }}
                    data-testid="owner-mrr-breakdown-total">
                  {_money(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function OwnerDashboard() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    fetchOverview({ range }).then(setData).catch((e) => { setError(e.message); setData(null); });
  }, [range]);

  if (error) {
    return (
      <div className="p-4" data-testid="owner-dashboard-error">
        <p className="text-[13px] text-[var(--rs-text-2)]">{error}</p>
        <Link to="/account" className="text-[12px] text-[var(--rs-amber)] hover:underline">Back to Account</Link>
      </div>
    );
  }
  if (!data) return <div className="p-4 text-[12px] text-[var(--rs-text-3)]" data-testid="owner-dashboard-loading">Loading owner dashboard…</div>;

  const sc     = _obj(data.summaryCards);
  const scUsers = _obj(sc.users);
  const scEnt   = _obj(sc.entitlementCurrent);
  const scCom   = _obj(sc.commercial);
  const scRev   = _obj(sc.revenue);
  const charts = _obj(data.charts);
  const fc     = _obj(data.foundingCard);
  const ah     = _obj(data.authHealth);
  const ph     = _obj(data.paymentHealth);
  const sh     = _obj(data.systemHealth);
  const ea     = _obj(data.entitlementAudit);
  const meta   = _obj(data.meta);

  const stages        = _arr(_obj(charts.conversionJourney).stages);
  const subMixBars    = _arr(_obj(charts.subscriptionMix).bars);
  const topFeatureBars = _arr(_obj(charts.topFeatures).bars);
  const premiumInterest = _obj(charts.premiumInterest);
  const premiumBars   = _arr(premiumInterest.bars);
  const revenueTrend  = _obj(charts.revenueTrend);
  const eaCounts      = _obj(ea.counts);
  const eaExceptions  = _arr(ea.exceptions);
  const byLabel = Object.fromEntries(stages.map((s) => [_obj(s).label, _obj(s).value]));
  const trackingBegins = meta.telemetryTrackingBeginsAt
    ? new Date(meta.telemetryTrackingBeginsAt)
    : null;

  return (
    <div className="p-4 space-y-6" data-testid="owner-dashboard-root">
      <div>
        <Link to="/account" className="inline-flex items-center gap-1 text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]" data-testid="owner-dashboard-back-link">
          <ArrowLeft className="w-3 h-3" /> Account
        </Link>
        <h1 className="font-heading font-black text-xl mt-1 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
          Owner Dashboard
        </h1>
        <p className="text-[12px] text-[var(--rs-text-3)]">
          Read-only · owner account only
          {trackingBegins && !Number.isNaN(trackingBegins.getTime())
            ? ` · tracking begins ${trackingBegins.toLocaleDateString()}`
            : ""}
        </p>
      </div>

      <div className="flex gap-2" data-testid="owner-dashboard-range-selector">
        {RANGES.map(([v, l]) => (
          <button
            key={v}
            data-testid={`owner-range-${v}`}
            onClick={() => setRange(v)}
            className="h-8 px-3 rounded-md text-[11px] font-semibold border"
            style={range === v
              ? { background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)", borderColor: "var(--rs-amber)" }
              : { color: "var(--rs-text-3)", borderColor: "var(--rs-border)" }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div>
        <h2 className="text-[11px] label-up text-[var(--rs-amber)] mb-2">Users</h2>
        <StatGrid items={[
          { label: "Total registered", value: scUsers.totalRegistered, testId: "stat-total-registered" },
          { label: "Logged in ever", value: scUsers.loggedInEver, testId: "stat-logged-in" },
          { label: "Never logged in", value: scUsers.neverLoggedIn, testId: "stat-never-logged-in" },
          { label: "Active last 24h", value: scUsers.activeLast24h, testId: "stat-active-24h" },
          { label: "Active last 7d", value: scUsers.activeLast7d, testId: "stat-active-7d" },
          { label: "Active last 30d", value: scUsers.activeLast30d, testId: "stat-active-30d" },
        ]} />
      </div>

      <div>
        <h2 className="text-[11px] label-up text-[var(--rs-amber)] mb-2">Entitlements (current)</h2>
        <StatGrid items={[
          { label: "Free", value: scEnt.free, testId: "stat-ent-free" },
          { label: "Active trials", value: scEnt.activeTrials, testId: "stat-ent-trials" },
          { label: "Active Pro", value: scEnt.activePro, testId: "stat-ent-pro" },
          { label: "Founding 500", value: scEnt.founding500, testId: "stat-ent-founding" },
          { label: "Past due", value: scEnt.pastDue, testId: "stat-ent-pastdue" },
          { label: "Cancelled — still entitled", value: scEnt.cancelledStillEntitled, testId: "stat-ent-cancelled" },
          { label: "No Pro access", value: scEnt.noProAccess, testId: "stat-ent-noaccess" },
        ]} />
      </div>

      <div>
        <h2 className="text-[11px] label-up text-[var(--rs-amber)] mb-2">Commercial (selected range)</h2>
        <StatGrid items={[
          { label: "Trials started", value: scCom.trialsStarted, testId: "stat-com-trials" },
          { label: "Checkout starts", value: scCom.checkoutStarts, testId: "stat-com-checkouts" },
          { label: "Successful new subscriptions", value: scCom.successfulNewSubscriptions, testId: "stat-com-newsubs" },
          { label: "Failed checkout/payment", value: scCom.failedPaymentOrCheckoutEvents, testId: "stat-com-failed" },
          { label: "Trial→paid conversions", value: scCom.trialToPaidConversions, testId: "stat-com-conversions" },
          { label: "Trial→paid rate", value: scCom.trialToPaidConversionRate != null ? `${scCom.trialToPaidConversionRate}%` : "Insufficient data", testId: "stat-com-rate" },
        ]} />
      </div>

      <div>
        <h2 className="text-[11px] label-up text-[var(--rs-amber)] mb-2">Revenue (recurring run-rate)</h2>
        <StatGrid items={[
          { label: "Current MRR", value: _money(scRev.currentMRR), testId: "stat-mrr", accent: "var(--rs-amber)" },
          { label: "Current ARR", value: _money(scRev.currentARR), testId: "stat-arr", accent: "var(--rs-amber)" },
        ]} />
        <MrrBreakdown rows={_arr(scRev.breakdown)} total={scRev.currentMRR} />
      </div>

      {/* Founding 500 */}
      <section className="card-rs p-4" data-testid="owner-founding-card">
        <h2 className="text-[11px] label-up text-[var(--rs-amber)] mb-2">Founding 500</h2>
        <StatGrid items={[
          { label: "Allocated", value: fc.placesAllocated },
          { label: "Remaining", value: fc.placesRemaining },
          { label: "Active monthly", value: fc.activeFoundersMonthly },
          { label: "Active annual", value: fc.activeFoundersAnnual },
          { label: "Cancelled/expired", value: fc.foundersCancelledOrExpired },
        ]} />
      </section>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="New Registrations" testId="chart-new-registrations">
          <TrendLineChart series={_arr(_obj(charts.newRegistrations).series)} xKey="date" yKey="value" />
        </ChartCard>
        <ChartCard title="Active Users (daily)" testId="chart-active-users">
          <TrendLineChart series={_arr(_obj(charts.activeUsers).series)} xKey="date" yKey="dau" color="#60A5FA" />
        </ChartCard>
        <ChartCard title="Current Entitlement Mix" testId="chart-entitlement-mix">
          <MixPieChart data={_arr(_obj(charts.entitlementMix).slices)} />
        </ChartCard>
        <ChartCard title="Subscription Mix" testId="chart-subscription-mix" empty={subMixBars.length === 0 ? "No active subscriptions yet." : null}>
          <MixBarChart data={subMixBars} />
        </ChartCard>
        <ChartCard title="Commercial Journey" testId="chart-commercial-journey">
          <FunnelBars stages={stages} />
          <p className="text-[10px] text-[var(--rs-text-3)] mt-2">Aggregate counts per stage in range — not a true per-user cohort funnel.</p>
        </ChartCard>
        <ChartCard title="Conversion Metrics" testId="chart-conversion-metrics">
          <div className="space-y-2 text-[12px]">
            <div className="flex justify-between"><span className="text-[var(--rs-text-3)]">Registration → Trial</span><span className="font-mono-rs">{pct(byLabel["Trial Started"], byLabel["Registered"])}</span></div>
            <div className="flex justify-between"><span className="text-[var(--rs-text-3)]">Trial → Paid</span><span className="font-mono-rs">{pct(byLabel["Paid Subscriber"], byLabel["Trial Started"])}</span></div>
            <div className="flex justify-between"><span className="text-[var(--rs-text-3)]">Checkout conversion</span><span className="font-mono-rs">{pct(byLabel["Paid Subscriber"], byLabel["Checkout Started"])}</span></div>
            <div className="flex justify-between"><span className="text-[var(--rs-text-3)]">Upgrade CTA → Checkout</span><span className="font-mono-rs">{pct(byLabel["Checkout Started"], byLabel["Upgrade Clicked"])}</span></div>
          </div>
        </ChartCard>
        <ChartCard title="Completed Subscription Revenue" testId="chart-revenue-trend" empty={!revenueTrend.available ? (revenueTrend.reason || "No data available yet.") : null}>
          {revenueTrend.available && Object.entries(_obj(revenueTrend.seriesByCurrency)).map(([cur, series]) => (
            <div key={cur} className="mb-2">
              <div className="text-[11px] text-[var(--rs-text-3)] mb-1">{cur}</div>
              <TrendLineChart series={_arr(series)} xKey="date" yKey="amount" color="#4CAF50" />
            </div>
          ))}
        </ChartCard>
        <ChartCard title="Most Used RigSentinel Features" testId="chart-top-features" empty={topFeatureBars.length === 0 ? "Insufficient data" : null}>
          <MixBarChart data={topFeatureBars} />
        </ChartCard>
        <ChartCard title="Premium Features Triggering Upgrade Interest" testId="chart-premium-interest" empty={premiumBars.length === 0 ? "Insufficient data" : null}>
          {premiumBars.length > 0 && <p className="text-[10px] text-[var(--rs-text-3)] mb-2">Metric: {premiumInterest.metricUsed}</p>}
          <MixBarChart data={premiumBars} />
        </ChartCard>
      </div>

      {/* Health panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <HealthPanel title="Authentication Health" testId="owner-auth-health" rows={[
          ["Successful logins", ah.successfulLogins], ["Failed logins", ah.failedLogins],
          ["Password reset requests", ah.passwordResetRequests], ["Password reset successes", ah.passwordResetSuccesses],
          ["Password reset failures", ah.passwordResetFailures], ["Magic-link requests", ah.magicLinkRequests],
          ["Magic-link successes", ah.magicLinkSuccesses], ["Magic-link failures", ah.magicLinkFailures],
        ]} />
        <HealthPanel title="Payment & Subscription Health" testId="owner-payment-health" rows={[
          ["Checkout starts", ph.checkoutStarts], ["Checkout successes", ph.checkoutSuccesses],
          ["Checkout failures", ph.checkoutFailures], ["Completed payments", ph.completedPayments],
          ["Failed payments", ph.failedPayments], ["Past-due subscriptions", ph.pastDueSubscriptions],
          ["Cancellations", ph.canceledSubscriptions], ["Expirations", ph.expiredSubscriptions],
          ["Last webhook received", _dateOr(ph.lastWebhookReceived, "Not available")],
          ["Paddle integration", ph.paddleIntegrationHealth],
        ]} />
        <HealthPanel title="System Health" testId="owner-system-health" rows={[
          ["App version", sh.appVersion], ["Backend/API status", sh.backendApiStatus],
          ["Auth service", sh.authServiceStatus], ["Email service", sh.emailServiceStatus],
          ["Paddle integration", sh.paddleIntegrationStatus], ["Analytics status", sh.analyticsStatus],
          ["Last entitlement refresh", _dateOr(sh.lastEntitlementRefresh, "Not monitored")],
        ]} />
      </div>

      {/* Entitlement audit */}
      <section className="card-rs p-4" data-testid="owner-entitlement-audit">
        <h3 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">Entitlement Health — Exceptions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {Object.entries(eaCounts).map(([k, v]) => (
            <div key={k} className="text-[11px]">
              <div className="text-[var(--rs-text-3)]">{k}</div>
              <div className="font-mono-rs text-base">{v}</div>
            </div>
          ))}
        </div>
        {eaExceptions.length === 0 ? (
          <p className="text-[12px] text-[var(--rs-text-3)]" data-testid="owner-audit-no-exceptions">No exceptions detected.</p>
        ) : (
          <div className="space-y-2">
            {eaExceptions.map((_ex, i) => {
              const ex = _obj(_ex);
              return (
              <div key={i} data-testid={`owner-audit-exception-${i}`} className="rounded-md border px-3 py-2 text-[11.5px]" style={{ borderColor: "var(--rs-danger)" }}>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="font-semibold text-[var(--rs-text)]">{ex.user}</span>
                  <span className="text-[var(--rs-text-3)]">{ex.userId}</span>
                </div>
                <div className="text-[var(--rs-danger)] mt-0.5">{ex.exception}</div>
                <div className="text-[var(--rs-text-3)]">Paddle: {ex.paddleStatus || "none"} · Effective: {ex.effectiveAccess}</div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      <OwnerUsersTable />
    </div>
  );
}
